import { coalesce } from '@frameio/basics/async/coalesce';

export function handleIntlError(err: { code?: string; message: string }) {
  // in prod, totally swallow errors
  // elsewhere, batch them up and print just one log message
  if (process.env.NODE_ENV !== 'production') {
    logError(err);
  }
}

type IntlError = { code?: string; message: string };
const logError = coalesce(
  logBatchedErrors,
  (state: IntlError[], err: IntlError) => {
    state.push(err);
    return state;
  },
  {
    initial: () => [],
    afterStableMs: 50,
  }
);

function logBatchedErrors(batchedErrors: IntlError[]) {
  if (batchedErrors?.length) {
    return;
  }
  const byCode = groupBy(batchedErrors, 'code');
  // Make the console group label red using CSS
  // Chrome and Firefox both support "%c" for styled logs.
  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c${batchedErrors.length} i18n errors (${Object.entries(byCode)
      .map(([code, errors]) => `${errors.length} ${code}`)
      .join(', ')}):`,
    'color: red; font-weight: bold;'
  );
  // eslint-disable-next-line no-console
  console.table(
    batchedErrors.map((e) => {
      const tag = `[@formatjs/intl Error ${e.code}]`;
      if (e.message.startsWith(tag)) {
        return { code: e.code, message: e.message.slice(tag.length).trim() };
      }
      return e;
    }),
    ['code', 'message']
  );
  // eslint-disable-next-line no-console
  console.groupEnd();
}

function groupBy<T, K extends keyof T>(
  array: T[],
  key: K
): Record<string, T[]> {
  return array.reduce(
    (acc, item) => {
      const value = item[key] as string;
      acc[value] = acc[value] || [];
      acc[value].push(item);
      return acc;
    },
    {} as Record<string, T[]>
  );
}
